/**
 * Package Assignment Service
 * Handles package assignment API calls for the simplified workflow
 */

import { api } from '../lib/axios';

export interface PackageAssignment {
  id: string;
  packageId: string;
  qrToken: string;
  packageType: 'pallet' | 'case' | 'unit';
  packageDetails: {
    contents: string;
    quantity: number;
    description?: string;
    value?: number;
  };
  status: 'assigned' | 'notified' | 'accepted' | 'expired';
  assignedAt: string;
  notifiedAt?: string;
  acceptedAt?: string;
  expiresAt: string;
  dealer: {
    id: string;
    name: string;
    mobileNumber: string;
  };
  manufacturer?: {
    name: string;
  };
}

export interface PackageAssignmentData {
  dealerId: string;
  packageId: string;
  qrToken: string;
  packageType: 'pallet' | 'case' | 'unit';
  packageDetails: {
    contents: string;
    quantity: number;
    description?: string;
    value?: number;
  };
  expiresInHours?: number;
}

export interface BulkAssignmentData {
  assignments: PackageAssignmentData[];
  notifyDealers?: boolean;
}

export interface AssignmentAnalytics {
  totalAssigned: number;
  totalAccepted: number;
  acceptanceRate: number;
  averageAcceptanceTimeHours: number;
  statusBreakdown: Record<string, number>;
  typeBreakdown: Record<string, number>;
}

export interface AssignmentFilters {
  status?: string;
  dealerId?: string;
  packageType?: string;
  limit?: number;
  offset?: number;
}

class PackageAssignmentService {
  /**
   * Assign a package to a dealer
   */
  async assignPackage(assignmentData: PackageAssignmentData): Promise<{ success: boolean; assignment?: PackageAssignment; error?: string }> {
    try {
      const response = await api.post('/package-assignments', assignmentData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to assign package'
      };
    }
  }

  /**
   * Assign multiple packages in bulk
   */
  async assignPackagesBulk(bulkData: BulkAssignmentData): Promise<{ 
    success: boolean; 
    results?: {
      successful: number;
      failed: number;
      assignments: PackageAssignment[];
      errors: Array<{ packageId: string; error: string }>;
    }; 
    error?: string 
  }> {
    try {
      const response = await api.post('/package-assignments/bulk', bulkData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to assign packages in bulk'
      };
    }
  }

  /**
   * Get package assignments for manufacturer
   */
  async getPackageAssignments(filters?: AssignmentFilters): Promise<{ 
    success: boolean; 
    assignments?: PackageAssignment[]; 
    pagination?: {
      total: number;
      limit: number;
      offset: number;
    };
    error?: string 
  }> {
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.dealerId) params.append('dealerId', filters.dealerId);
      if (filters?.packageType) params.append('packageType', filters.packageType);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const response = await api.get(`/package-assignments?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get package assignments'
      };
    }
  }

  /**
   * Update assignment status
   */
  async updateAssignmentStatus(assignmentId: string, status: string, metadata?: any): Promise<{ success: boolean; assignment?: PackageAssignment; error?: string }> {
    try {
      const response = await api.put(`/package-assignments/${assignmentId}/status`, { status, metadata });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update assignment status'
      };
    }
  }

  /**
   * Send notification to dealer about package assignment
   */
  async sendAssignmentNotification(assignmentId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await api.post(`/package-assignments/${assignmentId}/notify`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send notification'
      };
    }
  }

  /**
   * Get assignment analytics
   */
  async getAssignmentAnalytics(dateRange?: { startDate: Date; endDate: Date }): Promise<{ success: boolean; analytics?: AssignmentAnalytics; error?: string }> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());

      const response = await api.get(`/package-assignments/analytics?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get assignment analytics'
      };
    }
  }

  /**
   * Get package assignment by QR token (for dealer scanning)
   */
  async getAssignmentByQRToken(qrToken: string): Promise<{ success: boolean; assignment?: PackageAssignment; error?: string }> {
    try {
      const response = await api.get(`/package-assignments/qr/${qrToken}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get package assignment'
      };
    }
  }

  /**
   * Get packages assigned to a dealer (for dealer interface)
   */
  async getDealerPackages(dealerId: string, status?: string): Promise<{ success: boolean; packages?: PackageAssignment[]; error?: string }> {
    try {
      const params = status ? `?status=${status}` : '';
      const response = await api.get(`/dealers/${dealerId}/packages${params}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get dealer packages'
      };
    }
  }

  /**
   * Get available packages for assignment (not already assigned)
   */
  async getAvailablePackages(packageType?: string): Promise<{ success: boolean; packages?: any[]; error?: string }> {
    try {
      const params = packageType ? `?packageType=${packageType}` : '';
      const response = await api.get(`/package-assignments/available-packages${params}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get available packages'
      };
    }
  }

  /**
   * Update package assignment
   */
  async updateAssignment(assignmentId: string, updates: Partial<PackageAssignmentData>): Promise<{ success: boolean; assignment?: PackageAssignment; error?: string }> {
    try {
      const response = await api.put(`/package-assignments/${assignmentId}`, updates);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update assignment'
      };
    }
  }

  /**
   * Delete package assignment
   */
  async deleteAssignment(assignmentId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.delete(`/package-assignments/${assignmentId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete assignment'
      };
    }
  }

  /**
   * Generate destination label PDF for package
   */
  async generateDestinationLabel(assignmentId: string): Promise<{ success: boolean; pdfUrl?: string; error?: string }> {
    try {
      const response = await api.post(`/package-assignments/${assignmentId}/destination-label`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to generate destination label'
      };
    }
  }
}

export const packageAssignmentService = new PackageAssignmentService();